/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import './index.css';

import { useCallback, useEffect, useRef, useState } from 'react';
import * as React from 'react';
import { createPortal } from 'react-dom';

import {
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  $isRootOrShadowRoot,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND,
  KEY_MODIFIER_COMMAND,
  $createParagraphNode,
  DEPRECATED_$isGridSelection,
} from 'lexical';
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
  HeadingTagType,
} from '@lexical/rich-text';
import {
  $isListNode,
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import { $isCodeHighlightNode } from '@lexical/code';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  mergeRegister,
  $getNearestNodeOfType,
  $findMatchingParent,
} from '@lexical/utils';
import {
  $patchStyleText,
  $getSelectionStyleValueForProperty,
  $setBlocksType,
} from '@lexical/selection';

import { GoBold } from 'react-icons/go';
import { GrItalic } from 'react-icons/gr';
import { BsLink, BsTypeStrikethrough, BsTypeUnderline } from 'react-icons/bs';
import { TbSubscript, TbSuperscript } from 'react-icons/tb';

import { getDOMRangeRect } from '../../utils/getDOMRangeRect';
import { getSelectedNode } from '../../utils/getSelectedNode';
import { setFloatingElemPosition } from '../../utils/setFloatingElemPosition';
import { IS_APPLE } from '../../shared/environment';
import { sanitizeUrl } from '../../utils/url';
import { Dropdown, Menu } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import {
  backgroundColorOptions,
  blockTypeOptions,
  colorOptions,
} from '../../config';
import { styled } from '@stitches/configed';
import { TypographyStyled } from '../../TypographyStyled';
import { BlockType } from '../../types';
import vi from '../../locales/vi.json';

const ColorBoxStyled = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  width: 22,
  fontSize: 16,
  fontWeight: 500,
  borderRadius: 3,
  boxShadow: 'rgba(15, 15, 15, 0.1) 0px 0px 0px 1px inset',
  marginRight: 10,
  variants: {
    border: {
      false: {
        boxShadow: 'none',
      },
    },
  },
});
function TextFormatFloatingToolbar({
  editor,
  anchorElem,
  isLink,
  isBold,
  isItalic,
  isUnderline,
  isStrikethrough,
  isSubscript,
  isSuperscript,
  blockType,
  fontColor,
  bgColor,
}: {
  editor: LexicalEditor;
  anchorElem: HTMLElement;
  isBold: boolean;
  isItalic: boolean;
  isLink: boolean;
  isStrikethrough: boolean;
  isSubscript: boolean;
  isSuperscript: boolean;
  isUnderline: boolean;
  blockType: string;
  fontColor?: string;
  bgColor?: string;
}): JSX.Element {
  const popupCharStylesEditorRef = useRef<HTMLDivElement | null>(null);

  const formatParagraph = () => {
    editor.update(() => {
      const selection = $getSelection();
      if (
        $isRangeSelection(selection) ||
        DEPRECATED_$isGridSelection(selection)
      ) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  };
  const formatHeading = (headingSize: HeadingTagType) => {
    if (blockType !== headingSize) {
      editor.update(() => {
        const selection = $getSelection();
        if (
          $isRangeSelection(selection) ||
          DEPRECATED_$isGridSelection(selection)
        ) {
          $setBlocksType(selection, () => $createHeadingNode(headingSize));
        }
      });
    }
  };
  const formatBulletList = () => {
    if (blockType !== 'bullet') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };
  const formatCheckList = () => {
    if (blockType !== 'check') {
      editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };
  const formatNumberedList = () => {
    if (blockType !== 'number') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };
  const formatQuote = () => {
    if (blockType !== 'quote') {
      editor.update(() => {
        const selection = $getSelection();
        if (
          $isRangeSelection(selection) ||
          DEPRECATED_$isGridSelection(selection)
        ) {
          $setBlocksType(selection, () => $createQuoteNode());
        }
      });
    }
  };
  const formatBlockType = (type: BlockType) => {
    if (type === BlockType.Paragraph) {
      formatParagraph();
    } else if (
      type === BlockType.Heading1 ||
      type === BlockType.Heading2 ||
      type === BlockType.Heading3
    ) {
      formatHeading(type);
    } else if (type === BlockType.Bullet) {
      formatBulletList();
    } else if (type === BlockType.Check) {
      formatCheckList();
    } else if (type === BlockType.Number) {
      formatNumberedList();
    } else if (type === BlockType.Quote) {
      formatQuote();
    }
  };
  const applyStyleText = useCallback(
    (styles: Record<string, string | null>) => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $patchStyleText(selection, styles);
        }
      });
    },
    [editor],
  );

  const insertLink = useCallback(
    (e) => {
      e.preventDefault();
      if (!isLink) {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, 'https://');
      } else {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
      }
    },
    [editor, isLink],
  );

  const mouseMoveListener = (e: MouseEvent) => {
    if (
      popupCharStylesEditorRef?.current &&
      (e.buttons === 1 || e.buttons === 3)
    ) {
      if (popupCharStylesEditorRef.current.style.pointerEvents !== 'none') {
        const x = e.clientX;
        const y = e.clientY;
        const elementUnderMouse = document.elementFromPoint(x, y);

        if (!popupCharStylesEditorRef.current.contains(elementUnderMouse)) {
          // Mouse is not over the target element => not a normal click, but probably a drag
          popupCharStylesEditorRef.current.style.pointerEvents = 'none';
        }
      }
    }
  };

  function mouseUpListener() {
    if (popupCharStylesEditorRef?.current) {
      if (popupCharStylesEditorRef.current.style.pointerEvents !== 'auto') {
        // popupCharStylesEditorRef.current.style.pointerEvents = 'auto';
      }
    }
  }

  useEffect(() => {
    if (popupCharStylesEditorRef?.current) {
      document.addEventListener('mousemove', mouseMoveListener);
      document.addEventListener('mouseup', mouseUpListener);

      return () => {
        document.removeEventListener('mousemove', mouseMoveListener);
        document.removeEventListener('mouseup', mouseUpListener);
      };
    }
  }, [popupCharStylesEditorRef]);

  const updateTextFormatFloatingToolbar = useCallback(() => {
    const selection = $getSelection();

    const popupCharStylesEditorElem = popupCharStylesEditorRef.current;
    const nativeSelection = window.getSelection();

    if (popupCharStylesEditorElem === null) {
      return;
    }

    const rootElement = editor.getRootElement();
    if (
      selection !== null &&
      nativeSelection !== null &&
      !nativeSelection.isCollapsed &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode)
    ) {
      const rangeRect = getDOMRangeRect(nativeSelection, rootElement);

      setFloatingElemPosition(rangeRect, popupCharStylesEditorElem, anchorElem);
    }
  }, [editor, anchorElem]);

  useEffect(() => {
    const scrollerElem = anchorElem.parentElement;

    const update = () => {
      editor.getEditorState().read(() => {
        updateTextFormatFloatingToolbar();
      });
    };

    window.addEventListener('resize', update);
    if (scrollerElem) {
      scrollerElem.addEventListener('scroll', update);
    }

    return () => {
      window.removeEventListener('resize', update);
      if (scrollerElem) {
        scrollerElem.removeEventListener('scroll', update);
      }
    };
  }, [editor, updateTextFormatFloatingToolbar, anchorElem]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      updateTextFormatFloatingToolbar();
    });
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateTextFormatFloatingToolbar();
        });
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateTextFormatFloatingToolbar();
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor, updateTextFormatFloatingToolbar]);

  useEffect(() => {
    return editor.registerCommand(
      KEY_MODIFIER_COMMAND,
      (payload) => {
        const event: KeyboardEvent = payload;
        const { code, ctrlKey, metaKey } = event;

        if (code === 'KeyK' && (ctrlKey || metaKey)) {
          event.preventDefault();
          return editor.dispatchCommand(
            TOGGLE_LINK_COMMAND,
            sanitizeUrl('https://'),
          );
        }
        return false;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor, isLink]);

  return (
    <div ref={popupCharStylesEditorRef} className="floating-text-format-popup">
      {editor.isEditable() && (
        <>
          <Dropdown
            trigger={['click']}
            overlay={
              <Menu>
                <Menu.ItemGroup title={vi.turn_into}>
                  {blockTypeOptions.map(({ value, label }, idx) => {
                    return (
                      <Menu.Item
                        key={idx}
                        onClick={() => formatBlockType(value)}
                      >
                        <button
                          style={{
                            backgroundColor: 'transparent',
                          }}
                        >
                          <span>{label}</span>
                        </button>
                      </Menu.Item>
                    );
                  })}
                </Menu.ItemGroup>
              </Menu>
            }
          >
            <button
              className="popup-item spaced"
              title={vi.change_type}
              aria-label="Change type"
            >
              <div
                style={{
                  width: 50,
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                }}
              >
                {blockTypeOptions.find((x) => x.value === blockType)?.label ||
                  blockTypeOptions[0].label}
              </div>
              <DownOutlined style={{ marginLeft: 4 }} />
            </button>
          </Dropdown>
          <button
            onClick={(e) => {
              e.preventDefault();
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
            }}
            className={'popup-item spaced ' + (isBold ? 'active' : '')}
            title={IS_APPLE ? vi.bold + ' (⌘B)' : vi.bold + ' (Ctrl+B)'}
            aria-label={`Format text as bold. Shortcut: ${
              IS_APPLE ? '⌘B' : 'Ctrl+B'
            }`}
          >
            <GoBold className="format bold" />
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
            }}
            className={'popup-item spaced ' + (isItalic ? 'active' : '')}
            title={IS_APPLE ? vi.italic + ' (⌘I)' : vi.italic + ' (Ctrl+I)'}
            aria-label={`Format text as italics. Shortcut: ${
              IS_APPLE ? '⌘I' : 'Ctrl+I'
            }`}
          >
            <GrItalic className="format italic" />
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
            }}
            className={'popup-item spaced ' + (isUnderline ? 'active' : '')}
            title={
              IS_APPLE ? vi.underline + ' (⌘U)' : vi.underline + ' (Ctrl+U)'
            }
            aria-label={`Format text to underlined. Shortcut: ${
              IS_APPLE ? '⌘U' : 'Ctrl+U'
            }`}
          >
            <BsTypeUnderline className="format underline" />
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
            }}
            className={'popup-item spaced ' + (isStrikethrough ? 'active' : '')}
            title={vi.strikethrough}
            aria-label="Format text with a strikethrough"
          >
            <BsTypeStrikethrough className="format strikethrough" />
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'subscript');
            }}
            className={'popup-item spaced ' + (isSubscript ? 'active' : '')}
            title={vi.subscript}
            aria-label="Format Subscript"
          >
            <TbSubscript className="format subscript" />
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'superscript');
            }}
            className={'popup-item spaced ' + (isSuperscript ? 'active' : '')}
            title={vi.superscript}
            aria-label="Format Superscript"
          >
            <TbSuperscript className="format superscript" />
          </button>
          <button
            onClick={insertLink}
            className={'popup-item spaced ' + (isLink ? 'active' : '')}
            title={vi.insert_link}
            aria-label="Insert link"
          >
            <BsLink className="format link" />
          </button>
          <Dropdown
            trigger={['click']}
            overlay={
              <Menu>
                <Menu.ItemGroup title="COLOR">
                  {colorOptions.map(({ value, label }, idx) => {
                    return (
                      <Menu.Item
                        key={idx}
                        onClick={() => {
                          applyStyleText({
                            color: value,
                          });
                        }}
                      >
                        <TypographyStyled>
                          <button style={{ backgroundColor: 'transparent' }}>
                            <ColorBoxStyled
                              style={{
                                color: value || undefined,
                              }}
                            >
                              A
                            </ColorBoxStyled>
                            <span>{label}</span>
                          </button>
                        </TypographyStyled>
                      </Menu.Item>
                    );
                  })}
                </Menu.ItemGroup>
                <Menu.ItemGroup title="BACKGROUND">
                  {backgroundColorOptions.map(({ value, label }, idx) => {
                    return (
                      <Menu.Item
                        key={idx}
                        onClick={() => {
                          applyStyleText({
                            'background-color': value,
                          });
                        }}
                      >
                        <TypographyStyled>
                          <button style={{ backgroundColor: 'transparent' }}>
                            <ColorBoxStyled
                              style={{
                                backgroundColor: value || undefined,
                              }}
                            >
                              A
                            </ColorBoxStyled>
                            <span>{label}</span>
                          </button>
                        </TypographyStyled>
                      </Menu.Item>
                    );
                  })}
                </Menu.ItemGroup>
              </Menu>
            }
          >
            <button
              className="popup-item spaced"
              title={vi.change_color}
              aria-label="Change color"
            >
              <ColorBoxStyled
                border={false}
                style={{
                  color: fontColor,
                  backgroundColor: bgColor,
                }}
              >
                A
              </ColorBoxStyled>
              <DownOutlined />
            </button>
          </Dropdown>
        </>
      )}
    </div>
  );
}

function useFloatingTextFormatToolbar(
  editor: LexicalEditor,
  anchorElem: HTMLElement,
): JSX.Element | null {
  const [isText, setIsText] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isSubscript, setIsSubscript] = useState(false);
  const [isSuperscript, setIsSuperscript] = useState(false);
  const [blockType, setBlockType] = useState(BlockType.Paragraph);
  const [fontColor, setFontColor] = useState<string>();
  const [bgColor, setBgColor] = useState<string>();

  const updatePopup = useCallback(() => {
    editor.getEditorState().read(() => {
      // Should not to pop up the floating toolbar when using IME input
      if (editor.isComposing()) {
        return;
      }
      const selection = $getSelection();
      const nativeSelection = window.getSelection();
      const rootElement = editor.getRootElement();

      if (
        nativeSelection !== null &&
        (!$isRangeSelection(selection) ||
          rootElement === null ||
          !rootElement.contains(nativeSelection.anchorNode))
      ) {
        setIsText(false);
        return;
      }

      if (!$isRangeSelection(selection)) {
        return;
      }

      const anchorNode = selection.anchor.getNode();
      let element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : $findMatchingParent(anchorNode, (e) => {
              const parent = e.getParent();
              return parent !== null && $isRootOrShadowRoot(parent);
            });

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow();
      }
      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);

      // Update text format
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
      setIsSubscript(selection.hasFormat('subscript'));
      setIsSuperscript(selection.hasFormat('superscript'));
      setFontColor($getSelectionStyleValueForProperty(selection, 'color'));
      setBgColor(
        $getSelectionStyleValueForProperty(selection, 'background-color'),
      );

      const node = getSelectedNode(selection);

      // Update links
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }

      // Update block type
      if (elementDOM !== null) {
        // setSelectedElementKey(elementKey);
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType<ListNode>(
            anchorNode,
            ListNode,
          );
          const type = parentList
            ? parentList.getListType()
            : element.getListType();
          const blockType = blockTypeOptions.find((x) => x.value === type);
          if (blockType) {
            setBlockType(blockType.value);
          }
        } else {
          const type = $isHeadingNode(element)
            ? element.getTag()
            : element.getType();
          const blockType = blockTypeOptions.find((x) => x.value === type);
          if (blockType) {
            setBlockType(blockType.value);
          }
        }
      }

      if (
        !$isCodeHighlightNode(selection.anchor.getNode()) &&
        selection.getTextContent() !== ''
      ) {
        setIsText($isTextNode(node));
      } else {
        setIsText(false);
      }

      const rawTextContent = selection.getTextContent().replace(/\n/g, '');
      if (!selection.isCollapsed() && rawTextContent === '') {
        setIsText(false);
        return;
      }
    });
  }, [editor]);

  useEffect(() => {
    document.addEventListener('selectionchange', updatePopup);
    return () => {
      document.removeEventListener('selectionchange', updatePopup);
    };
  }, [updatePopup]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(() => {
        updatePopup();
      }),
      editor.registerRootListener(() => {
        if (editor.getRootElement() === null) {
          setIsText(false);
        }
      }),
    );
  }, [editor, updatePopup]);

  if (!isText || isLink) {
    return null;
  }

  return createPortal(
    <TextFormatFloatingToolbar
      editor={editor}
      anchorElem={anchorElem}
      isLink={isLink}
      isBold={isBold}
      isItalic={isItalic}
      isStrikethrough={isStrikethrough}
      isSubscript={isSubscript}
      isSuperscript={isSuperscript}
      isUnderline={isUnderline}
      blockType={blockType}
      fontColor={fontColor}
      bgColor={bgColor}
    />,
    anchorElem,
  );
}

export default function FloatingTextFormatToolbarPlugin({
  anchorElem = document.body,
}: {
  anchorElem?: HTMLElement;
}): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  return useFloatingTextFormatToolbar(editor, anchorElem);
}
